import type { Fault } from '@/types';

// 港机码头 3 类核心故障
// 严格按已批准的时间轴 SOP 编排；expertPath 通过 hints[] 落地为可读的"专家路径"
// 易错点检测通过 hints 里"⚠️" 前缀 + steps 中 correct=false 的错误动作实现

export const portcraneFaults: Fault[] = [
  // ============= 故障 1：起升机构超载（hoist-overload）中危 =============
  // 场景：起升载荷达到 72t（额定 65t），超载 10%。若不降速并核查重量，钢丝绳可能过载断丝
  // 专家 S 级路径 5 步约 40 秒：
  //   T=0s   view HOIST / SENSOR-LOAD
  //   T=5s   action HOIST-SPD 55 → 20 m/min（降速减冲击）
  //   T=10s  action 吊具下降到安全高度
  //   T=20s  核查集装箱实际重量
  //   T=40s  确认 LOAD-PCT < 100% 通关
  {
    id: 'PCF001',
    name: '起升机构超载（hoist-overload）',
    description:
      '起升机构载荷达到 72t（额定 65t），超载 10%。可能是集装箱超重或载荷传感器漂移。' +
      '如不及时降速并核查重量，钢丝绳可能过载断丝。',
    cause: '集装箱实际重量超过申报重量 + 载荷传感器校准偏差，导致起升载荷持续超出额定值。',
    affectedEquipments: ['HOIST', 'SPREADER', 'SENSOR-LOAD'],
    symptoms: [
      { equipmentId: 'HOIST', param: 'HOIST-LOAD', paramName: '起升载荷', value: 72, unit: 't', normal: '≤65', trend: 'up' },
      { equipmentId: 'SENSOR-LOAD', param: 'LOAD-PCT', paramName: '载荷百分比', value: 110.8, unit: '%', normal: '<100', trend: 'up' },
      { equipmentId: 'HOIST', param: 'HOIST-SPD', paramName: '起升速度', value: 55, unit: 'm/min', normal: '30-60', trend: 'up' },
      { equipmentId: 'SPREADER', param: 'SPREADER-LOAD', paramName: '吊具载荷', value: 72, unit: 't', normal: '≤65', trend: 'up' },
    ],
    steps: [
      // 专家 S 级路径 —— 严格按时间轴顺序
      { id: 's1', action: '观察载荷传感器读数确认超载', correct: true, order: 1 },
      { id: 's2', action: '起升降速到 20 m/min', correct: true, order: 2 },
      { id: 's3', action: '将吊具下降到安全高度', correct: true, order: 3 },
      { id: 's4', action: '核查集装箱实际重量', correct: true, order: 4 },
      { id: 's5', action: '确认 LOAD-PCT < 100% 通关', correct: true, order: 5 },
      // 易错动作 —— 直接急停：集装箱摆动可能脱锁
      { id: 's6', action: '直接急停起升机构（错误：集装箱摆动可能脱锁）', correct: false, order: 6 },
      { id: 's7', action: '不降速继续满速起升（错误：钢丝绳应力累积断丝）', correct: false, order: 7 },
    ],
    hints: [
      '专家 S 级路径：view HOIST/SENSOR-LOAD(0s) → 降速到 20m/min(5s) → 吊具降到安全高度(10s) → 核查重量(20s) → 确认 LOAD-PCT<100%(40s) → 通关',
      '起升超载抢救原则：先降速减冲击，再核查重量 —— 顺序不能反',
      '⚠️ 易错点：直接急停 = 集装箱摆动可能脱锁',
    ],
    divergence: {
      drivers: [
        // 不干预时起升载荷持续上升到 80t 上限；吊具载荷同步上升（同一载荷链）
        { equipmentId: 'HOIST', param: 'HOIST-LOAD', rate: 0.3, cap: 80, delaySec: 10 },
        { equipmentId: 'SPREADER', param: 'SPREADER-LOAD', rate: 0.3, cap: 80, delaySec: 10 },
        // 注：HOIST-SPD 不自动下降，学员不手动降速则钢丝绳应力持续累积
      ],
    },
  },

  // ============= 故障 2：小车跑偏（trolley-deviation）高难 =============
  // 场景：小车跑偏量达 35mm（正常 <20mm），轨道一侧磨损导致导向轮偏磨，不及时校正可能脱轨
  // 专家 S 级路径 5 步约 40 秒：
  //   T=0s   view TROLLEY / RAIL
  //   T=5s   action TROLLEY-SPD 45 → 15 m/min（降速减偏磨）
  //   T=10s  action 微调小车纠偏（TROLLEY 工艺按钮）
  //   T=30s  观察跑偏量回落
  //   T=40s  确认 TROLLEY-DEV < 20 通关
  {
    id: 'PCF002',
    name: '小车跑偏（trolley-deviation）',
    description:
      '小车运行跑偏量达 35mm（正常 <20mm），轨道一侧磨损导致导向轮偏磨。' +
      '如果不及时校正并降速，小车可能脱轨。',
    cause: '轨道一侧磨损超标 + 导向轮间隙过大，小车运行时单侧受力导致跑偏累积。',
    affectedEquipments: ['TROLLEY', 'RAIL', 'BOOM'],
    symptoms: [
      { equipmentId: 'TROLLEY', param: 'TROLLEY-DEV', paramName: '小车跑偏量', value: 35, unit: 'mm', normal: '<20', trend: 'up' },
      { equipmentId: 'TROLLEY', param: 'TROLLEY-SPD', paramName: '小车速度', value: 45, unit: 'm/min', normal: '20-60', trend: 'up' },
      { equipmentId: 'RAIL', param: 'RAIL-DEV', paramName: '轨道偏差', value: 8, unit: 'mm', normal: '<5', trend: 'up' },
      { equipmentId: 'RAIL', param: 'RAIL-WEAR', paramName: '磨损量', value: 12, unit: 'mm', normal: '<10', trend: 'up' },
    ],
    steps: [
      // 专家 S 级路径 —— 5 步纠偏处置
      { id: 's1', action: '观察小车跑偏量和轨道偏差', correct: true, order: 1 },
      { id: 's2', action: '小车降速到 15 m/min', correct: true, order: 2 },
      { id: 's3', action: '微调小车纠偏（TROLLEY 工艺按钮）', correct: true, order: 3 },
      { id: 's4', action: '观察跑偏量回落', correct: true, order: 4 },
      { id: 's5', action: '确认 TROLLEY-DEV < 20 通关', correct: true, order: 5 },
      // 易错动作 —— 不降速直接纠偏：导向轮冲击加剧
      { id: 's6', action: '不降速直接纠偏（错误：导向轮冲击可能加剧）', correct: false, order: 6 },
      { id: 's7', action: '直接急停小车（错误：吊具摆动可能脱锁）', correct: false, order: 7 },
    ],
    hints: [
      '专家 S 级路径：view TROLLEY/RAIL(0s) → 降速到 15m/min(5s) → 微调纠偏(10s) → 观察回落(30s) → 确认 TROLLEY-DEV<20(40s) → 通关',
      '小车跑偏抢救原则：先降速减偏磨，再纠偏 —— 顺序不能反',
      '⚠️ 易错点：不降速直接纠偏 = 导向轮冲击可能加剧',
    ],
    divergence: {
      drivers: [
        // 不降速时跑偏量持续上升到 50mm 上限；轨道偏差也同步恶化
        { equipmentId: 'TROLLEY', param: 'TROLLEY-DEV', rate: 0.5, cap: 50, delaySec: 8 },
        { equipmentId: 'RAIL', param: 'RAIL-DEV', rate: 0.1, cap: 15, delaySec: 12 },
      ],
    },
  },

  // ============= 故障 3：大车风报警（gantry-wind-alarm）极危 =============
  // 场景：风速达 28m/s（报警 >25m/s），大车仍在运行未制动，必须立即制动 + 收臂 + 锚定
  // 专家 S 级路径 6 步约 50 秒：
  //   T=0s   view SENSOR-WIND / GANTRY
  //   T=3s   action GANTRY-BRAKE 0 → 1（急停制动）
  //   T=8s   action BOOM-ANGLE 0 → 60°（收臂减风载）
  //   T=15s  部署锚定装置
  //   T=20s  pause 30s（等风速变化）
  //   T=50s  确认 GANTRY-BRAKE=1 且 WIND-SPD<25 通关
  {
    id: 'PCF003',
    name: '大车风报警（gantry-wind-alarm）',
    description:
      '⚠️ 极危故障！风速达到 28m/s（报警阈值 25m/s），大车必须立即制动并锚定。' +
      '如果不及时锁定，强风可能推动起重机沿轨道滑行导致脱轨碰撞。',
    cause: '突发阵风 + 大车锚定装置未提前部署，整机在大风下面临被推动滑行的风险。',
    affectedEquipments: ['GANTRY', 'SENSOR-WIND', 'BOOM', 'POWER'],
    symptoms: [
      { equipmentId: 'SENSOR-WIND', param: 'WIND-SPD', paramName: '风速', value: 28, unit: 'm/s', normal: '<25', trend: 'up' },
      { equipmentId: 'GANTRY', param: 'GANTRY-SPD', paramName: '大车速度', value: 20, unit: 'm/min', normal: '0-30', trend: 'up' },
      { equipmentId: 'GANTRY', param: 'GANTRY-BRAKE', paramName: '制动状态', value: 0, unit: '', normal: '0=释放', trend: 'down' },
      { equipmentId: 'BOOM', param: 'BOOM-ANGLE', paramName: '俯仰角', value: 0, unit: '°', normal: '0°水平', trend: 'down' },
    ],
    steps: [
      // 专家 S 级路径 —— 6 步紧急处置
      { id: 's1', action: '观察风速仪读数确认报警', correct: true, order: 1 },
      { id: 's2', action: '大车急停制动（GANTRY-BRAKE=1）', correct: true, order: 2 },
      { id: 's3', action: '大梁俯仰收起到 60° 减风载', correct: true, order: 3 },
      { id: 's4', action: '部署锚定装置', correct: true, order: 4 },
      { id: 's5', action: '等 30 秒观察风速变化', correct: true, order: 5 },
      { id: 's6', action: '确认 GANTRY-BRAKE=1 且 WIND-SPD<25 通关', correct: true, order: 6 },
      // 易错动作 —— 先收臂不制动：起重机被风吹跑
      { id: 's7', action: '先收臂不制动（错误：起重机被风吹跑）', correct: false, order: 7 },
      { id: 's8', action: '只制动不锚定（错误：制动器长时间受力可能失效）', correct: false, order: 8 },
    ],
    hints: [
      '专家 S 级路径：view SENSOR-WIND/GANTRY(0s) → 急停制动 BRAKE=1(3s) → 大梁收起到 60°(8s) → 部署锚定(15s) → 等 30s → 确认 BRAKE=1 且 WIND-SPD<25(50s) → 通关',
      '大风抢救铁律：先制动，再收臂，最后锚定 —— 顺序不能乱',
      '⚠️ 易错点：先收臂不制动 = 起重机被风吹跑',
    ],
    divergence: {
      drivers: [
        // 不制动时风速持续上升到 35m/s 上限；大车被风吹动沿轨道被动位移
        { equipmentId: 'SENSOR-WIND', param: 'WIND-SPD', rate: 0.2, cap: 35, delaySec: 5 },
        { equipmentId: 'GANTRY', param: 'GANTRY-POS', rate: 0.1, cap: 500, delaySec: 8 },
      ],
    },
  },

  // ============= 故障 4：吊具摇摆失控（spreader-sway）=============
  // 场景：吊具摇摆角升至 12°，小车跑偏 80mm，大车速度波动
  // 专家 S 级路径 4 步：降大车速度 → 查防摇 PID → 调减摇钢丝绳 → 切手动微调
  {
    id: 'PC004',
    name: '吊具摇摆失控',
    description:
      '集装箱吊具在大车移动时产生剧烈摇摆，摇摆角超过安全限值，威胁作业安全。',
    cause: '大车加速过急，防摇系统PID参数失谐，减摇钢丝绳张力不均。',
    affectedEquipments: ['SPREADER', 'TROLLEY', 'GANTRY'],
    symptoms: [
      { equipmentId: 'SPREADER', param: 'SWAY-ANG',    paramName: '摇摆角',     value: 12, unit: '°',     normal: '0-5',   trend: 'up' },
      { equipmentId: 'TROLLEY',  param: 'TROLLEY-DEV', paramName: '小车跑偏量', value: 80, unit: 'mm',    normal: '0-20',  trend: 'up' },
      { equipmentId: 'GANTRY',   param: 'GANTRY-SPD',  paramName: '大车速度',   value: 45, unit: 'm/min', normal: '0-30',  trend: 'unstable' },
    ],
    steps: [
      { id: 's1', action: '立即降低大车运行速度',     correct: true,  order: 1 },
      { id: 's2', action: '检查防摇系统PID参数',      correct: true,  order: 2 },
      { id: 's3', action: '调整减摇钢丝绳张力',       correct: true,  order: 3 },
      { id: 's4', action: '切换到手动微调模式',       correct: true,  order: 4 },
      { id: 's5', action: '提高大车速度快速就位',     correct: false, order: 5 },
      { id: 's6', action: '解除防摇系统',             correct: false, order: 6 },
      { id: 's7', action: '松开减摇钢丝绳',           correct: false, order: 7 },
    ],
    hints: [
      '摇摆角超过10度很危险，先减速',
      '防摇PID失谐了会越摇越大，赶紧调参数',
      '减摇钢丝绳松紧不一也会加剧摇摆',
    ],
    divergence: {
      drivers: [
        // 不干预时摇摆角持续扩大，可能拉断减摇钢丝绳
        { equipmentId: 'SPREADER', param: 'SWAY-ANG', rate: 0.15, cap: 20, delaySec: 10 },
      ],
    },
  },

  // ============= 故障 5：PLC 通信中断（plc-comm-loss）=============
  // 场景：PLC 通信状态归零、报警数飙至 99、起升速度归零无法控制
  // 专家 S 级路径 4 步：按急停 → 查通信链路 → 切备用通道 → 重启通信模块
  {
    id: 'PCF005',
    name: 'PLC 通信中断',
    description:
      '港机主PLC与变频器通信中断，多个机构失控，需紧急停机处理。',
    cause: 'PLC通信总线接口老化，工业以太网交换机端口故障导致通信链路中断。',
    affectedEquipments: ['PLC', 'HOIST', 'GANTRY'],
    symptoms: [
      { equipmentId: 'PLC',   param: 'PLC-STAT',  paramName: '通信状态', value: 0,  unit: '',      normal: '1=正常',  trend: 'down' },
      { equipmentId: 'PLC',   param: 'PLC-ALARM', paramName: '报警数',   value: 99, unit: '个',    normal: '0-5',    trend: 'up' },
      { equipmentId: 'HOIST', param: 'HOIST-SPD', paramName: '起升速度', value: 0,  unit: 'm/min', normal: '30-60',  trend: 'down' },
    ],
    steps: [
      { id: 's1', action: '立即按下急停按钮',           correct: true,  order: 1 },
      { id: 's2', action: '检查PLC通信链路状态',        correct: true,  order: 2 },
      { id: 's3', action: '切换到备用通信通道',         correct: true,  order: 3 },
      { id: 's4', action: '重启PLC通信模块',            correct: true,  order: 4 },
      { id: 's5', action: '忽略通信报警继续作业',       correct: false, order: 5 },
      { id: 's6', action: '拆除急停按钮',               correct: false, order: 6 },
      { id: 's7', action: '提高运行速度抢时间',         correct: false, order: 7 },
    ],
    hints: [
      'PLC通信断了机构会失控，先急停保安全',
      '检查通信链路从交换机端口开始排查',
      '有备用通道就切过去，别等主通道修好',
    ],
    divergence: {
      drivers: [
        // 不干预时 PLC 报警数持续攀升，系统进入严重告警状态
        { equipmentId: 'PLC', param: 'PLC-ALARM', rate: 2, cap: 99, delaySec: 5 },
      ],
    },
  },
];
